// #3708 ask (d) — the result of "Recover UX artefacts" (POST /artefacts/projects/:id/recover-phase-2-5).
// It used to be a green one-line summary that vanished after 6 s, showed success even when
// nothing could be extracted, and never said why. It now stays until dismissed, is a warning
// whenever something could not be extracted, and lists each type's reason.
import { structureRebuiltSourceLabel } from './chat/StructureRebuiltNotice';

export interface Phase25RecoveryResponse {
  // #3903 — structureRebuiltFrom: the agent's JSON was unusable and the structure was rebuilt from upstream.
  recovered: { type: string; artefactId: string; approved?: boolean; structureRebuiltFrom?: string }[];
  alreadyPresent: string[];
  // #3903 (delta R1/R2) — rebuildNote: why a rebuild the button asked for could not run (its own field).
  noContent: { type: string; reason: string; rebuildNote?: string }[];
}

export interface Phase25RecoveryNoticeModel {
  tone: 'success' | 'warning';
  summary: string;
  details: string[];
}

const TYPE_LABEL: Record<string, string> = {
  screen_inventory: 'Screen Inventory',
  screen_spec: 'Screen Specifications',
  component_catalogue: 'Component Catalogue',
};

/** The server's reason codes are fixed strings; anything unknown is shown as given. */
function reasonText(reason: string): string {
  if (/^heading found but JSON block missing/i.test(reason)) {
    return 'the agent wrote it in chat, but without a valid JSON code block, so it cannot be saved from that message';
  }
  if (/^no message contained the artefact heading/i.test(reason)) return 'no message in the UX chat contains it';
  if (/^no UX agent session/i.test(reason)) return 'this project has no UX Design chat';
  if (/^UX session has zero assistant messages/i.test(reason)) return 'the UX Design chat has no agent replies';
  return reason;
}

export function buildPhase25RecoveryNotice(result: Phase25RecoveryResponse): Phase25RecoveryNoticeModel {
  const parts: string[] = [];
  if (result.recovered.length > 0) parts.push(`Recovered ${result.recovered.length}`);
  if (result.alreadyPresent.length > 0) parts.push(`${result.alreadyPresent.length} already present`);
  if (result.noContent.length > 0) parts.push(`${result.noContent.length} not extractable`);
  const details = [
    ...result.recovered.map((r) => {
      // #3903 — say when the structure was rebuilt, naming the source (a known source only).
      const from = structureRebuiltSourceLabel(r.structureRebuiltFrom);
      return from
        ? `${TYPE_LABEL[r.type] ?? r.type}: recovered as a draft. The agent's reply had no usable JSON block, so the structured data was rebuilt from the ${from}. Check it on the artefact page before approving.`
        : `${TYPE_LABEL[r.type] ?? r.type}: recovered as a draft.`;
    }),
    ...result.noContent.map(
      // #3903 (R1/R2 P2-1) — when the button's rebuild could not run, say why (rebuildNote).
      (n) => `${TYPE_LABEL[n.type] ?? n.type}: not extractable — ${reasonText(n.reason)}${n.rebuildNote ? `, and it could not be rebuilt: ${n.rebuildNote}` : ''}. Ask the UX Design agent to produce it again.`,
    ),
  ];
  return {
    tone: result.noContent.length > 0 ? 'warning' : 'success',
    summary: parts.length > 0 ? parts.join(' · ') : 'Nothing to recover — UX artefacts are up to date.',
    details,
  };
}

export function Phase25RecoveryNotice({ notice, onDismiss }: { notice: Phase25RecoveryNoticeModel; onDismiss: () => void }) {
  const warning = notice.tone === 'warning';
  return (
    <div
      role={warning ? 'alert' : 'status'}
      data-testid="phase25-recovery-notice"
      data-tone={notice.tone}
      className={`mx-4 mt-3 text-sm rounded-lg px-4 py-3 flex-shrink-0 border ${
        warning ? 'text-amber-800 bg-amber-50 border-amber-300' : 'text-emerald-700 bg-emerald-50 border-emerald-100'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold">{notice.summary}</p>
        <button type="button" onClick={onDismiss} aria-label="Dismiss the recovery result" className="hover:opacity-70">
          ×
        </button>
      </div>
      {notice.details.length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {notice.details.map((d, i) => (
            // R1 P3 — index key: two details can carry the same text.
            <li key={i}>{d}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
